import React from 'react';
import { 
  MessageSquare, ImageIcon, Upload, Bold, Italic, Code, 
  LinkIcon, List, Eye, Users, X
} from 'lucide-react';

export const MessageEditor = ({
  messageText,
  setMessageText,
  messageType,
  setMessageType,
  parseMode,
  setParseMode,
  imagePreview,
  handleImageSelect,
  handleImageRemove,
  insertFormatting,
  preparePreview,
  setShowUserSelector,
  sending
}) => {
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-blue-400" />
        Редактор сообщения
      </h2>

      {/* Тип сообщения */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Тип сообщения
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => setMessageType('text')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              messageType === 'text'
                ? 'bg-blue-900/30 text-blue-300 border border-blue-700'
                : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
            }`}
          >
            Текстовое
          </button>
          <button
            onClick={() => setMessageType('notification')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              messageType === 'notification'
                ? 'bg-purple-900/30 text-purple-300 border border-purple-700'
                : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
            }`}
          >
            Уведомление
          </button>
          <button
            onClick={() => setMessageType('alert')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              messageType === 'alert'
                ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700'
                : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
            }`}
          >
            Важное
          </button>
        </div>
      </div>

      {/* Режим форматирования */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Форматирование
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => setParseMode('HTML')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              parseMode === 'HTML'
                ? 'bg-green-900/30 text-green-300 border border-green-700'
                : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
            }`}
          >
            HTML
          </button>
          <button
            onClick={() => setParseMode('Markdown')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              parseMode === 'Markdown'
                ? 'bg-green-900/30 text-green-300 border border-green-700'
                : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
            }`}
          >
            Markdown
          </button>
        </div>
      </div>

      {/* Загрузка изображения */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <ImageIcon className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-medium text-gray-300">Изображение</span>
          <span className="text-xs text-gray-500">(опционально, до 10 МБ)</span>
        </div>
        
        <div className="flex items-center gap-2">
          {!imagePreview ? (
            <>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-300 text-sm rounded-lg hover:bg-gray-600 transition-colors cursor-pointer"
              >
                <Upload className="w-4 h-4" />
                Загрузить фото
              </label>
              <span className="text-xs text-gray-500">PNG, JPG, GIF</span>
            </>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-10 h-10 rounded object-cover border border-gray-600"
                />
                <button
                  onClick={handleImageRemove}
                  className="absolute -top-1 -right-1 p-0.5 bg-red-900/80 text-white rounded-full hover:bg-red-800 transition-colors"
                  title="Удалить"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <span className="text-sm text-gray-300 truncate">
                Изображение выбрано
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Панель форматирования */}
      <div className="mb-2 flex items-center gap-1 flex-wrap">
        <button
          onClick={() => insertFormatting('bold')}
          className="p-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
          title="Жирный"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => insertFormatting('italic')}
          className="p-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
          title="Курсив"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => insertFormatting('code')}
          className="p-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
          title="Код"
        >
          <Code className="w-4 h-4" />
        </button>
        <button
          onClick={() => insertFormatting('link')}
          className="p-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
          title="Ссылка"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => insertFormatting('list')}
          className="p-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
          title="Список"
        >
          <List className="w-4 h-4" />
        </button>
        <div className="h-6 w-px bg-gray-700 mx-1"></div>
        <span className="text-xs text-gray-500">
          {parseMode === 'HTML' ? 'HTML режим' : 'Markdown режим'}
        </span>
      </div>

      {/* Текст сообщения */}
      <div className="mb-4">
        <textarea
          id="message-textarea"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder={imagePreview ? "Введите подпись к изображению..." : "Введите текст сообщения..."}
          className="w-full px-4 py-3 bg-gray-900 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-h-[150px] font-mono"
        />
      </div>

      {/* Подсказки */}
      <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700 mb-4">
        <p className="text-xs text-gray-400">
          <span className="text-blue-400 font-medium">HTML:</span> &lt;b&gt;жирный&lt;/b&gt;, &lt;i&gt;курсив&lt;/i&gt;, &lt;a href="url"&gt;ссылка&lt;/a&gt;
        </p>
        <p className="text-xs text-gray-400 mt-1">
          <span className="text-green-400 font-medium">Markdown:</span> *жирный*, _курсив_, [текст](ссылка)
        </p>
      </div>

      {/* Кнопки действий */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={preparePreview}
          disabled={(!messageText.trim() && !imagePreview) || sending}
          className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          <Eye className="w-4 h-4" />
          Предпросмотр
        </button>
        <button
          onClick={() => setShowUserSelector(true)}
          className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
        >
          <Users className="w-4 h-4" />
          Выбрать получателей
        </button>
      </div>
    </div>
  );
};